import { useEffect, useRef, useState, useCallback } from "react";

interface CelebrationOverlayProps {
  numbers: number[];
  onClose: () => void;
  onCopy: () => void;
}

const BALL_COLORS_CSS = [
  "from-amber-400 to-amber-600",
  "from-rose-400 to-rose-600",
  "from-emerald-400 to-emerald-600",
  "from-sky-400 to-sky-600",
  "from-violet-400 to-violet-600",
  "from-orange-400 to-orange-600",
];

const BALL_COLORS_HEX = [
  { fill: "#fbbf24", stroke: "#b45309" },
  { fill: "#f87171", stroke: "#b91c1c" },
  { fill: "#34d399", stroke: "#047857" },
  { fill: "#60a5fa", stroke: "#1d4ed8" },
  { fill: "#c084fc", stroke: "#7c3aed" },
  { fill: "#fb923c", stroke: "#c2410c" },
];

const RIBBON_COLORS = [
  "#fbbf24", "#f87171", "#34d399", "#60a5fa",
  "#c084fc", "#fb923c", "#f472b6", "#a78bfa",
  "#fcd34d", "#6ee7b7", "#93c5fd", "#fca5a5",
];

interface Ribbon {
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  opacity: number;
  wave: number;
  waveSpeed: number;
}

function drawShareCard(canvas: HTMLCanvasElement, numbers: number[]) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const W = 600;
  const H = 380;
  canvas.width = W;
  canvas.height = H;

  const bgGrad = ctx.createLinearGradient(0, 0, W, H);
  bgGrad.addColorStop(0, "#1a1a2e");
  bgGrad.addColorStop(0.5, "#16213e");
  bgGrad.addColorStop(1, "#1a1a2e");
  ctx.fillStyle = bgGrad;
  ctx.beginPath();
  ctx.roundRect(0, 0, W, H, 20);
  ctx.fill();

  ctx.strokeStyle = "#f59e0b";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect(2, 2, W - 4, H - 4, 18);
  ctx.stroke();

  const glowGrad = ctx.createRadialGradient(W / 2, H * 0.3, 30, W / 2, H * 0.3, 300);
  glowGrad.addColorStop(0, "rgba(251, 191, 36, 0.08)");
  glowGrad.addColorStop(1, "rgba(251, 191, 36, 0)");
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, 0, W, H);

  ctx.beginPath();
  ctx.arc(W / 2, 55, 28, 0, Math.PI * 2);
  const tGrad = ctx.createRadialGradient(W / 2 - 5, 50, 3, W / 2, 55, 28);
  tGrad.addColorStop(0, "#fcd34d");
  tGrad.addColorStop(1, "#d97706");
  ctx.fillStyle = tGrad;
  ctx.fill();
  ctx.font = "bold 28px sans-serif";
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("\uD83C\uDFC6", W / 2, 56);

  ctx.font = "bold 22px Orbitron, sans-serif";
  ctx.fillStyle = "#fbbf24";
  ctx.fillText("PINBALL", W / 2 - 38, 105);
  ctx.fillStyle = "#f97316";
  ctx.fillText("SIX", W / 2 + 52, 105);

  ctx.font = "14px sans-serif";
  ctx.fillStyle = "rgba(251, 191, 36, 0.7)";
  ctx.fillText("\uD540\uBCFC\uB85C \uBF51\uC740 \uC624\uB298\uC758 \uD589\uC6B4 \uBC88\uD638", W / 2, 130);

  const sorted = [...numbers].sort((a, b) => a - b);
  const ballR = 34;
  const totalW = sorted.length * (ballR * 2 + 14) - 14;
  const startX = (W - totalW) / 2 + ballR;
  const ballY = 190;

  sorted.forEach((num, i) => {
    const cx = startX + i * (ballR * 2 + 14);
    const col = BALL_COLORS_HEX[i];

    // Shadow
    ctx.beginPath();
    ctx.arc(cx + 3, ballY + 3, ballR, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.fill();

    // Glow
    ctx.beginPath();
    ctx.arc(cx, ballY, ballR + 6, 0, Math.PI * 2);
    ctx.fillStyle = col.fill + "30";
    ctx.fill();

    // Ball - reduced reflection (matte look)
    ctx.beginPath();
    ctx.arc(cx, ballY, ballR, 0, Math.PI * 2);
    const bGrad = ctx.createRadialGradient(cx - 6, ballY - 6, 2, cx + 4, ballY + 4, ballR);
    bGrad.addColorStop(0, col.fill);
    bGrad.addColorStop(0.7, col.fill);
    bGrad.addColorStop(1, col.stroke);
    ctx.fillStyle = bGrad;
    ctx.fill();
    ctx.strokeStyle = col.stroke;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Subtle highlight (smaller, less bright)
    ctx.beginPath();
    ctx.arc(cx - 8, ballY - 8, 5, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.18)";
    ctx.fill();

    // Number - bigger and bolder
    ctx.font = "bold 26px Orbitron, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 5;
    ctx.fillText(String(num), cx, ballY + 1);
    ctx.shadowBlur = 0;
  });

  // Divider
  ctx.strokeStyle = "rgba(251, 191, 36, 0.2)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 250);
  ctx.lineTo(W - 80, 250);
  ctx.stroke();

  // Date
  const now = new Date();
  const dateStr = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, "0")}.${String(now.getDate()).padStart(2, "0")}`;
  ctx.font = "13px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.fillText(dateStr, W / 2, 278);

  // Footer - bigger and yellow
  ctx.font = "bold 18px sans-serif";
  ctx.fillStyle = "#fbbf24";
  ctx.fillText("\uAD6C\uB9E4\uB294 \uC2E0\uC911\uD788, \uAD6D\uBC25\uC740 \uACFC\uAC10\uD788. \uD83C\uDF72", W / 2, 318);

  ctx.font = "11px sans-serif";
  ctx.fillStyle = "rgba(251, 191, 36, 0.5)";
  ctx.fillText("PINBALL SIX", W / 2, 355);
}

export default function CelebrationOverlay({ numbers, onClose, onCopy }: CelebrationOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const shareCanvasRef = useRef<HTMLCanvasElement>(null);
  const ribbonsRef = useRef<Ribbon[]>([]);
  const rafRef = useRef<number>(0);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [toast, setToast] = useState("");

  const sorted = [...numbers].sort((a, b) => a - b);

  const handleCopy = useCallback(async () => {
    try {
      await onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  }, [onCopy]);

  const handleSaveImage = useCallback(async () => {
    if (!shareCanvasRef.current || numbers.length < 6) return;
    setSaving(true);
    try {
      drawShareCard(shareCanvasRef.current, numbers);
      const dataUrl = shareCanvasRef.current.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `pinball-lotto-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      setToast("\uC774\uBBF8\uC9C0 \uC800\uC7A5 \uC644\uB8CC! \uD83D\uDCF8");
      setTimeout(() => setToast(""), 2500);
    } catch {
      setToast("\uC800\uC7A5 \uC2E4\uD328 \uD83D\uDE22");
      setTimeout(() => setToast(""), 2500);
    } finally {
      setSaving(false);
    }
  }, [numbers]);

  const fallbackCopy = useCallback(async (s: number[], url: string) => {
    const t = `\uD83C\uDFB0 \uD540\uBCFC \uB85C\uB610 \uBC88\uD638!\n\uD83C\uDFC6 ${s.join(" - ")}\n\n\uB098\uB3C4 \uD540\uBCFC\uB85C \uB85C\uB610 \uBC88\uD638 \uBF51\uC544\uBCF4\uAE30 \u{1F449}\n${url}`;
    try {
      await navigator.clipboard.writeText(t);
      setToast("\uACF5\uC720 \uD14D\uC2A4\uD2B8 \uBCF5\uC0AC \uC644\uB8CC! \uCE74\uD1A1\uC5D0 \uBD99\uC5EC\uB123\uAE30 \uD558\uC138\uC694 \uD83D\uDCCB");
      setTimeout(() => setToast(""), 3000);
    } catch {
      setToast("\uBCF5\uC0AC \uC2E4\uD328 \uD83D\uDE22");
      setTimeout(() => setToast(""), 2500);
    }
  }, []);

  const handleShareKakao = useCallback(async () => {
    if (numbers.length < 6) return;
    setSharing(true);
    const sorted2 = [...numbers].sort((a, b) => a - b);
    const text = `\uD83C\uDFB0 \uB7ED\uD0A4\uD540\uBCFC6 \uBC88\uD638!\n\uD83C\uDFC6 ${sorted2.join(" - ")}\n\n\uB098\uB3C4 \uD540\uBCFC\uB85C \uB85C\uB610 \uBC88\uD638 \uBF51\uC544\uBCF4\uAE30 \u{1F449}`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        const shareData: {
          title?: string;
          text?: string;
          url?: string;
          files?: File[];
        } = {
          title: "\uB7ED\uD0A4\uD540\uBCFC6 - \uC624\uB298\uC758 \uD589\uC6B4 \uBC88\uD638",
          text,
          url: shareUrl,
        };
        if (shareCanvasRef.current) {
          drawShareCard(shareCanvasRef.current, numbers);
          const blob = await new Promise<Blob | null>((res) =>
            shareCanvasRef.current!.toBlob((b) => res(b), "image/png")
          );
          if (blob) {
            const file = new File([blob], "pinball-lotto.png", { type: "image/png" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              shareData.files = [file];
            }
          }
        }
        await navigator.share(shareData);
        setToast("\uACF5\uC720 \uC644\uB8CC! \uD83C\uDF89");
        setTimeout(() => setToast(""), 2500);
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          await fallbackCopy(sorted2, shareUrl);
        }
      }
    } else {
      await fallbackCopy(sorted2, shareUrl);
    }
    setSharing(false);
  }, [numbers, fallbackCopy]);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const createRibbons = () => {
      const newRibbons: Ribbon[] = [];
      for (let i = 0; i < 120; i++) {
        newRibbons.push({
          x: Math.random() * canvas.width,
          y: -Math.random() * canvas.height * 1.5,
          vx: (Math.random() - 0.5) * 2,
          vy: 1.5 + Math.random() * 3,
          width: 6 + Math.random() * 10,
          height: 14 + Math.random() * 20,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.15,
          color: RIBBON_COLORS[Math.floor(Math.random() * RIBBON_COLORS.length)],
          opacity: 0.7 + Math.random() * 0.3,
          wave: Math.random() * Math.PI * 2,
          waveSpeed: 0.02 + Math.random() * 0.04,
        });
      }
      ribbonsRef.current = newRibbons;
    };
    createRibbons();

    const animate = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ribbonsRef.current.forEach((r) => {
        r.y += r.vy;
        r.x += r.vx + Math.sin(r.wave) * 0.8;
        r.rotation += r.rotationSpeed;
        r.wave += r.waveSpeed;
        if (r.y > canvas.height + 50) {
          r.y = -30;
          r.x = Math.random() * canvas.width;
        }
        ctx.save();
        ctx.translate(r.x, r.y);
        ctx.rotate(r.rotation);
        ctx.globalAlpha = r.opacity;
        ctx.fillStyle = r.color;
        ctx.beginPath();
        ctx.moveTo(-r.width / 2, -r.height / 2);
        ctx.quadraticCurveTo(0, -r.height / 4, r.width / 2, -r.height / 2);
        ctx.lineTo(r.width / 3, r.height / 2);
        ctx.quadraticCurveTo(0, r.height / 3, -r.width / 3, r.height / 2);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = r.opacity * 0.3;
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.ellipse(0, -r.height / 4, r.width / 4, r.height / 6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"
          }`}
        onClick={onClose}
      />
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
      {/* Hidden canvas for image generation */}
      <canvas ref={shareCanvasRef} className="hidden" />

      <div
        className={`relative z-10 max-w-md w-full mx-4 transition-all duration-700 ${visible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-75 translate-y-10"
          }`}
      >
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 border-2 border-amber-500/50 shadow-2xl shadow-amber-500/20 text-center relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-24 bg-orange-500/10 rounded-full blur-3xl" />

          <div className="relative mb-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-xl shadow-amber-500/30 animate-bounce">
              <i className="ri-trophy-fill text-white text-3xl" />
            </div>
          </div>

          <h2
            className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 mb-1"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            CONGRATULATIONS!
          </h2>
          <p className="text-amber-400/80 text-sm mb-6">
            🎉 6개 번호가 모두 선택되었습니다! 🎉
          </p>

          <div className="flex items-center justify-center gap-2 mb-4">
            {sorted.map((num, i) => (
              <div
                key={i}
                className={`w-10 h-10 rounded-full bg-gradient-to-br ${BALL_COLORS_CSS[i]} text-white font-bold flex items-center justify-center shadow-lg text-xs`}
                style={{
                  fontFamily: "Orbitron, sans-serif",
                  animation: `popIn 0.4s ease-out ${i * 0.1}s both`,
                }}
              >
                {num}
              </div>
            ))}
          </div>

          <div className="bg-gray-800/80 rounded-xl px-4 py-2.5 mb-5 border border-amber-600/20 inline-block">
            <span
              className="text-amber-300 font-bold text-sm tracking-widest"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              {sorted.join(" - ")}
            </span>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2.5 mb-3">
            <button
              type="button"
              onClick={handleCopy}
              className={`py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer transition-all duration-200 shadow-lg ${copied
                ? "bg-emerald-500 text-white shadow-emerald-500/30"
                : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-gray-900 shadow-amber-500/30"
                }`}
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              <i className={`${copied ? "ri-check-line" : "ri-file-copy-line"} text-sm`} />
              {copied ? "복사됨!" : "번호 복사"}
            </button>
            <button
              type="button"
              onClick={handleShareKakao}
              disabled={sharing}
              className="py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer transition-all duration-200 shadow-lg bg-[#FEE500] hover:bg-[#FDD835] text-gray-900 disabled:bg-gray-700 disabled:text-gray-500"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              {sharing ? (
                <i className="ri-loader-4-line text-sm animate-spin" />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3C6.48 3 2 6.58 2 10.94c0 2.8 1.86 5.27 4.66 6.67-.15.56-.96 3.6-.99 3.83 0 0-.02.17.09.24.11.06.24.01.24.01.32-.04 3.7-2.44 4.28-2.86.56.08 1.14.12 1.72.12 5.52 0 10-3.58 10-7.94C22 6.58 17.52 3 12 3z" fill="#3C1E1E" />
                </svg>
              )}
              {sharing ? "공유 중..." : "카톡 공유"}
            </button>
            <button
              type="button"
              onClick={handleSaveImage}
              disabled={saving}
              className="py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer transition-all duration-200 shadow-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white disabled:bg-gray-700 disabled:text-gray-500"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              <i className={`${saving ? "ri-loader-4-line animate-spin" : "ri-image-fill"} text-sm`} />
              {saving ? "저장 중..." : "이미지 저장"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="py-2.5 px-4 rounded-xl bg-gray-700 hover:bg-gray-600 text-gray-200 font-bold text-xs flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer transition-all duration-200"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              <i className="ri-close-line text-sm" />
              닫기
            </button>
          </div>

          {/* Toast in overlay */}
          {toast && (
            <div className="py-2 px-4 bg-amber-500/20 border border-amber-500/40 rounded-xl">
              <span className="text-amber-300 text-xs font-bold">{toast}</span>
            </div>
          )}

          <p className="text-gray-500 text-xs mt-3">행운을 빕니다! 🍀</p>
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
